/* eslint-disable @typescript-eslint/no-unused-vars */
import { UpdateProfileFormSection } from "@/design-system/Organisms";
import { AdvancedStepper } from "@/design-system/Organisms/AdvanceStepper";
import type { StepComponentProps, StepConfig } from "@/design-system/Organisms/AdvanceStepper/types";
import CheckoutConfirmationSection from "@/design-system/Organisms/CheckoutConfirmationSection";
import CheckoutHeaderSection from "@/design-system/Organisms/CheckoutHeaderSection";
import CheckoutOrderSummarySection from "@/design-system/Organisms/CheckoutOrderSummarySection";
import CheckoutPaymentDetailsSection from "@/design-system/Organisms/CheckoutPaymentDetailsSection";
import CheckoutReviewOrderSection, { type UserInfo } from "@/design-system/Organisms/CheckoutReviewOrderSection";
import { ROUTES } from "@/routes/routes.config";
import { useCheckoutService } from "@/rtkQuery/features/hooks/useCheckoutService";
import { usePatientInformationService } from "@/rtkQuery/features/hooks/usePatientInformationService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useCallback, useState } from "react";
import { useCartService } from "@/rtkQuery/features/hooks/useCartService";
import { AppEmptyState } from "@/design-system/Feedbacks";

interface PaymentInfo {
    paymentMethod?: string;
    cardNumber?: string;
    cardHolderName?: string;
    expiryDate?: string;
    cvv?: string;
    cardHash?: string;
    status?: string;
    timestamp?: string;
    [key: string]: any;
}

interface ReviewData {
    reviewDate?: string;
    notes?: string;
    isApproved?: boolean;
    approvedBy?: string;
    shippingMethod?: string;
    termsAccepted?: boolean;
    [key: string]: any;
}

interface OrderConfirmation {
    orderId?: string;
    confirmationNumber?: string;
    orderDate?: string;
    [key: string]: any;
}

interface CheckoutData {
    userInfo?: UserInfo;
    paymentInfo?: PaymentInfo;
    reviewData?: ReviewData;
    orderConfirmation?: OrderConfirmation;
}

export interface AdvancedStepperRef {
    goToStep: (stepId: string) => Promise<void>;
    setEditingSection: (sectionId: string) => void;
    getCurrentStep: () => string | null;
    getData: () => CheckoutData;
    validateCurrentStep: () => Promise<boolean>;
}

const UserInfoStep: React.FC<StepComponentProps<CheckoutData>> = ({
    data,
    onDataChange,
    onNext,
    onPrevious,
    isLoading = false,
    errors = []
}) => {
    const { savedCardData } = useCheckoutService();

    const hasSavedPaymentData = useCallback(() => {
        return savedCardData?.Hash && savedCardData.Hash.trim() !== '';
    }, [savedCardData]);

    const handleSubmitSuccess = useCallback(({ formData }: { formData: any }) => {
        if (!formData.firstName || !formData.lastName) {
            console.error('Missing required user information');
            return;
        }

        const userInfo: UserInfo = {
            ...formData,
            FullName: `${formData.firstName} ${formData.lastName}`,
            DateOfBirth: formData.dateOfBirth,
            Address: formData.address,
            City: formData.city,
            State: formData.state,
            ZipCode: formData.zipCode
        };

        onDataChange({ userInfo } as Partial<CheckoutData>);

        if (hasSavedPaymentData()) {
            const paymentInfo: PaymentInfo = {
                paymentMethod: 'Credit Card',
                cardNumber: savedCardData?.LastFour || '',
                cardHolderName: savedCardData?.BillingAddress?.FullName || '',
                cardHash: savedCardData?.Hash || '',
                status: 'Success',
                timestamp: new Date().toISOString()
            };

            onDataChange({
                userInfo,
                paymentInfo
            } as Partial<CheckoutData>);
        }

        onNext();
    }, [onDataChange, onNext, hasSavedPaymentData, savedCardData]);

    const handleSubmitError = useCallback((err: any) => {
        console.error("User info submission error:", err);
    }, []);

    return (
        <UpdateProfileFormSection
            title="Your Information"
            saveButtonText={hasSavedPaymentData() ? "Continue to Review Order" : "Continue to Payment"}
            showChangePasswordButton={false}
            showEmailField={false}
            submitButtonVariant="secondary"
            showTitle={false}
            onSubmitSuccess={handleSubmitSuccess}
            onSubmitError={handleSubmitError}
        />
    );
};

const PaymentStep: React.FC<StepComponentProps<CheckoutData>> = ({
    data,
    onDataChange,
    onNext,
    onPrevious,
    isLoading = false,
    errors = []
}) => {
    const handleComplete = useCallback((paymentData: any) => {
        if (paymentData) {
            const enhancedPaymentData: PaymentInfo = {
                ...paymentData,
                status: paymentData.status || 'Success',
                timestamp: new Date().toISOString()
            };
            onDataChange({ paymentInfo: enhancedPaymentData } as Partial<CheckoutData>);
        }
        onNext();
    }, [onDataChange, onNext]);

    return (
        <CheckoutPaymentDetailsSection
            onComplete={handleComplete}
        />
    );
};

const ReviewStep: React.FC<StepComponentProps<CheckoutData>> = ({
    data,
    onDataChange,
    onNext,
    onPrevious,
    isLoading = false,
    errors = []
}) => {
    const handleComplete = useCallback((reviewData: any) => {
        const reviewInfo: ReviewData = {
            reviewDate: new Date().toISOString(),
            notes: reviewData?.notes || 'Customer approved order',
            isApproved: true,
            approvedBy: data?.userInfo?.email || 'user@example.com',
            shippingMethod: 'Expedited 5-7 Business Days',
            termsAccepted: true,
            ...reviewData
        };

        onDataChange({ reviewData: reviewInfo } as Partial<CheckoutData>);
        onNext();
    }, [data, onDataChange, onNext]);

    return (
        <CheckoutReviewOrderSection
            onComplete={handleComplete}
            userInfo={data?.userInfo || {}}
            paymentInfo={data?.paymentInfo}
        />
    );
};

const ConfirmationStep: React.FC<StepComponentProps<CheckoutData>> = (
    //     {
    //     data,
    //     onDataChange,
    //     onNext,
    //     onPrevious,
    //     isLoading = false,
    //     errors = []
    // }
) => {
    return <CheckoutConfirmationSection />;
};

const CheckoutPageTemplate: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getPatientInformation } = usePatientInformationService();
    const stepperRef = useRef<AdvancedStepperRef>(null);
    const { savedCardData } = useCheckoutService();
    const { cartData } = useCartService();
    const [currentStepId, setCurrentStepId] = useState<string>('user-info');

    const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
    useEffect(() => {
        const stepParam = searchParams.get('step');
        const sectionParam = searchParams.get('section');

        if (stepParam && stepperRef.current) {
            const validSteps = ['user-info', 'payment', 'review', 'confirmation'];
            if (validSteps.includes(stepParam)) {
                setTimeout(() => {
                    stepperRef.current?.goToStep(stepParam);
                }, 100);
            }
        }

        if (sectionParam && stepperRef.current) {
            setTimeout(() => {
                stepperRef.current?.setEditingSection(sectionParam);
            }, 100);
        }
    }, [searchParams]);

    const hasSavedPaymentData = useCallback(() => {
        return savedCardData?.Hash && savedCardData.Hash.trim() !== '';
    }, [savedCardData]);

    const checkoutSteps: StepConfig<CheckoutData>[] = [
        {
            id: 'user-info',
            title: 'Your Information',
            description: 'Enter your personal and shipping details',
            component: UserInfoStep,
            validation: async (data: CheckoutData) => {
                const userInfo = data.userInfo;
                const errors: string[] = [];

                if (!userInfo?.firstName && !userInfo?.FullName) {
                    errors.push('First name is required');
                }
                if (!userInfo?.lastName && !userInfo?.FullName) {
                    errors.push('Last name is required');
                }
                if (!userInfo?.email) {
                    errors.push('Email is required');
                }
                if (!userInfo?.phone && !userInfo?.phoneNumber) {
                    errors.push('Phone number is required');
                }

                return {
                    isValid: errors.length === 0,
                    errors: errors.length > 0 ? errors : undefined
                };
            }
        },
        {
            id: 'payment',
            title: 'Payment Information',
            description: 'Select payment method and enter details',
            component: PaymentStep,
            validation: async (data: CheckoutData) => {
                const paymentInfo = data.paymentInfo;

                if (!paymentInfo?.paymentMethod) {
                    return { isValid: false, errors: ['Payment method is required'] };
                }

                if (!paymentInfo?.cardHash && !paymentInfo?.cardNumber) {
                    return { isValid: false, errors: ['Payment information is incomplete'] };
                }

                return { isValid: true };
            },
            shouldSkip: (data: CheckoutData) => {
                return !!hasSavedPaymentData() && !!data.userInfo?.firstName;
            }
        },
        {
            id: 'review',
            title: 'Review Order',
            description: 'Review your order before placing it',
            component: ReviewStep,
            validation: async (data: CheckoutData) => {
                if (!data.userInfo?.firstName && !data.userInfo?.FullName) {
                    return { isValid: false, errors: ['User information is required'] };
                }

                if (!data.paymentInfo?.paymentMethod && !hasSavedPaymentData()) {
                    return { isValid: false, errors: ['Payment information is required'] };
                }

                return { isValid: true };
            }
        },
        {
            id: 'confirmation',
            title: 'Confirmation',
            description: 'Order confirmation and receipt',
            component: ConfirmationStep,
            onEnter: async (data: CheckoutData, helpers?: any) => {
                try {
                    const orderConfirmation: OrderConfirmation = {
                        orderId: `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        confirmationNumber: `CONF-${Math.random().toString(36).substr(2, 12).toUpperCase()}`,
                        orderDate: new Date().toISOString()
                    };

                    helpers?.onDataChange?.({ orderConfirmation } as Partial<CheckoutData>);
                } catch (error) {
                    console.error('Failed to create order:', error);
                    throw new Error('Failed to place order. Please try again.');
                }
            }
        }
    ];

    const initialData: CheckoutData = {
        userInfo: {
            ...getPatientInformation,
            FullName: getPatientInformation?.FullName || undefined,
            DateOfBirth: getPatientInformation?.DateOfBirth || undefined,
            Address: getPatientInformation?.Address || undefined,
            City: getPatientInformation?.City || undefined,
            State: getPatientInformation?.State || undefined,
            ZipCode: getPatientInformation?.ZipCode || undefined
        },
        paymentInfo: hasSavedPaymentData()
            ? {
                paymentMethod: "Credit Card",
                cardNumber: savedCardData?.LastFour || "",
                cardHolderName: savedCardData?.BillingAddress?.FullName || "",
                cardHash: savedCardData?.Hash || "",
                status: "Success",
                timestamp: new Date().toISOString()
            }
            : {},
        reviewData: {},
        orderConfirmation: {}
    };

    const profileData = {
        userInfo: {
            ...getPatientInformation,
            FullName: getPatientInformation?.FullName,
            DateOfBirth: getPatientInformation?.DateOfBirth,
            Address: getPatientInformation?.Address,
            City: getPatientInformation?.City,
            State: getPatientInformation?.State,
            ZipCode: getPatientInformation?.ZipCode,
        },
        paymentInfo: {
            cardHash: savedCardData?.Hash || '',
            cardNumber: savedCardData?.LastFour || '',
            cardHolderName: savedCardData?.BillingAddress?.FullName || '',
        },
        reviewData: {
            reviewDate: '',
            notes: '',
            isApproved: false
        }
    };

    const handleComplete = useCallback(async (data: CheckoutData) => {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('step', 'confirmation');
        window.history.replaceState({}, '', newUrl.toString());
    }, []);

    const handleBack = useCallback(() => {
        navigate(ROUTES.PRODUCTS_LIST);
    }, [navigate]);

    const handleStepChange = useCallback((fromStep: string | null, toStep: string, data: CheckoutData) => {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('step', toStep);
        window.history.replaceState({}, '', newUrl.toString());

        setCurrentStepId(toStep);
    }, []);

    const handleError = useCallback((error: Error, step: string) => {
        console.error(`Error in step ${step}:`, error);
    }, []);

    const stepperProps = {
        steps: checkoutSteps,
        initialData,
        onComplete: handleComplete,
        onBack: handleBack,
        allowStepNavigation: true,
        persistKey: "checkout-progress",
        autoSave: true,
        profileData,
        onStepChange: handleStepChange,
        onError: handleError,
        showProgress: false,
        showSteps: false,
        className: "",
        appliedCoupon,
        onCouponApplied: setAppliedCoupon,
    };

    const [showEmptyCartMessage, setShowEmptyCartMessage] = useState(false);

    const isCartEmpty = !cartData?.products || cartData.products.length === 0;

    useEffect(() => {
        let redirectTimer: NodeJS.Timeout;

        if (isCartEmpty) {
            if (currentStepId !== 'confirmation') {
                setShowEmptyCartMessage(true);
                redirectTimer = setTimeout(() => {
                    navigate(ROUTES.PRODUCTS_LIST);
                }, 3000);
            } else {
                setShowEmptyCartMessage(false);
            }
        } else {
            setShowEmptyCartMessage(false);
        }

        return () => {
            if (redirectTimer) {
                clearTimeout(redirectTimer);
            }
        };
    }, [isCartEmpty, searchParams, navigate, currentStepId]);


    return (
        <>
            <CheckoutHeaderSection />


            <main id="content">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col lg:flex-row gap-y-8 lg:gap-y-0 my-[3.125rem] lg:my-[6.25rem]">
                        {showEmptyCartMessage ?
                            (<div className="w-fit mx-auto">
                                <AppEmptyState className="px-4" message="It looks like you don't have any items in your cart. You'll be redirected to the products page in a moment." isRedirection={true} />
                            </div>) : <>
                                <div className="w-full lg:w-[58.333333%]">
                                    <AdvancedStepper
                                        ref={stepperRef}
                                        {...stepperProps}
                                    />
                                </div>
                                <div className="w-full lg:w-[33.333333%] lg:ml-[8.333333%]">
                                    <CheckoutOrderSummarySection currentStep={currentStepId} appliedCoupon={appliedCoupon} />
                                </div>
                            </>}
                    </div>

                </div>
            </main>

        </>
    );
};

export default CheckoutPageTemplate;